<template>
  <div>
    <SectionSeparatorWrapper
      :separator="false"
      :headerText="showRebateSectionHeader ? 'Rebate' : ''"
      :headerRequiredFieldsText="showRequiredFieldsText ? 'indicates required field' : null"
    >
      <slot name="pageTitle"></slot>
      <div class="form-section">
        <FormField labelText="Do you want to apply a rebate?" :helperText="helperText">
          <div class="input-grid">
            <ul class="radio-group large">
              <li v-for="option in applyRebateOptions" :key="option.id">
                <input :disabled="isChangeCover" type="radio" :id="option.id" :value="option.value" name="rebateApplied" v-model="rebateForm.isRebateApplied" />
                <label :for="option.id">{{ option.displayValue }}</label>
              </li>
            </ul>
          </div>
        </FormField>
        <FormField labelText="Are you covered by this policy?" v-if="rebateForm.isRebateApplied">
          <div class="input-grid">
            <ul class="radio-group large">
              <li v-for="option in policyCoveredOptions" :key="option.id">
                <input type="radio" :id="option.id" :value="option.value" name="policyCovered" v-model="rebateForm.isPolicyCovered" />
                <label :for="option.id">{{ option.displayValue }}</label>
              </li>
            </ul>
          </div>
        </FormField>
        <!-- single/ couple/ family/ family+/ single family/ single family+ cover -->
        <template v-if="!isKidsOnly">
          <FormField v-if="rebateForm.isPolicyCovered === false">
            <div v-html="policyCoveredInformationText.other"></div>
          </FormField>
        </template>
        <!-- Kids Only cover -->
        <template v-else>
          <FormField v-if="rebateForm.isPolicyCovered === true">
            <div v-html="policyCoveredInformationText.isKidsOnly"></div>
          </FormField>
        </template>
        <FormField
          labelText="Are all people on the policy eligible for Medicare?"
          v-if="(rebateForm.isPolicyCovered === true && !isKidsOnly) || (rebateForm.isPolicyCovered === false && isKidsOnly)"
        >
          <div class="input-grid">
            <ul class="radio-group large">
              <li v-for="option in medicareEligibilityOptions" :key="option.id">
                <input type="radio" :id="option.id" :value="option.value" name="medicareEligible" v-model="rebateForm.isMedicareEligible" />
                <label :for="option.id">{{ option.displayValue }}</label>
              </li>
            </ul>
          </div>
        </FormField>
        <FormField v-if="rebateForm.isMedicareEligible === false"><div v-html="policyEligibilityInformationText"></div></FormField>
        <template v-if="isRebateAppliedAndEligible">
          <FormField labelText="Rebate applicant" :helperText="rebateApplicantFieldHelperText">
            <DropdownSelect
              :id="'rebateApplicant'"
              name="rebateApplicant"
              :items="getApplicantsList"
              :initialSelect="getInitialSelectedApplicant"
              v-model="v$.rebateForm.rebateApplicant.$model"
              :disabled="disableRebateApplicantDropdown"
            />
          </FormField>
          <FormField :required="false" labelText="Rebate reduction commencement date">
            <p aria-label="Rebate reduction commencement date">{{ rebateForm.currentDate }}</p>
          </FormField>
        </template>
      </div>
    </SectionSeparatorWrapper>
    <SectionSeparatorWrapper v-if="isRebateAppliedAndEligible" :separator="false">
      <MedicareCardForm @updateMedicareCard="getMedicareCardData" :medicareCardData="rebateForm.medicareCard">
        <template #header>
          <div class="header">
            <h3 class="h6 sub-heading-underlined no-margin">Medicare Card</h3>
            <p v-html="medicareCardSectionSubText" />
          </div>
        </template>
      </MedicareCardForm>
    </SectionSeparatorWrapper>
    <SectionSeparatorWrapper v-if="isRebateAppliedAndEligible" :separator="false">
      <h3 class="h6 sub-heading-underlined">Rebate information</h3>
      <div class="form-section">
        <FormField labelText="My circumstances" forId="myCircumstances" :required="true">
          <div class="input-grid">
            <ul class="radio-group large">
              <li v-for="item in myCircumstancesOptions" :key="item.value">
                <input
                  type="radio"
                  :disabled="isChangeCover"
                  v-model="v$.rebateForm.myCircumstances.$model"
                  :id="item.value"
                  :value="item.value"
                  name="myCircumstances"
                  :error="v$.rebateForm.myCircumstances.$error"
                  errorMessage="Please include a response for your circumstances."
                />
                <label :for="item.value">{{ item.displayValue }}</label>
              </li>
            </ul>
          </div>
        </FormField>
        <FormField
          labelText="My household income for the purposes of determining rebate is"
          forId="rebateTier"
          :required="true"
          v-if="getRebateTierList.length"
          :helperText="householdIncomeFieldHelperText"
        >
          <DropdownSelect
            id="rebateTier"
            :disabled="isChangeCover"
            :items="getRebateTierList"
            v-model="v$.rebateForm.rebateTier.$model"
            :initialSelect="getRebateTierInitialSelect(rebateTierLevel)"
          />
        </FormField>
        <slot name="commencesDate"></slot>
        <FormField labelText="Privacy and your personal information" :required="false">
          <template v-if="this.$slots.privacyStatement">
            <slot name="privacyStatement" />
          </template>
          <template v-else>
            <p>
              The privacy and security of your personal information is important to us, and is protected by law. We need to collect this information so we can process and manage
              your applications and payments, and provide services to you. We only share your information with other parties where you have agreed, or where law allows or requires
              it.
            </p>
            <p>
              For more information, go to
              <a href="https://www.servicesaustralia.gov.au/your-right-to-privacy" target="_blank">servicesaustralia.gov.au/privacy</a>
            </p>
          </template>
        </FormField>
        <FormField>
          <div class="field checkbox">
            <input
              type="checkbox"
              id="rebateAcceptance"
              v-model="rebateForm.rebateAcceptance"
              @change="v$.rebateForm.rebateAcceptance.$touch()"
              @keyup.enter.space="rebateForm.rebateAcceptance = !rebateForm.rebateAcceptance"
            />
            <label for="rebateAcceptance">
              Do you declare that the information that you have provided is complete and correct?
              <span class="required-input">*</span>
            </label>
            <label for="rebateAcceptance" class="error" v-if="v$.rebateForm.rebateAcceptance.$error && v$.rebateForm.rebateAcceptance.sameAs.$invalid">
              Please include a response for Declaration.
            </label>
          </div>
        </FormField>
        <FormField>
          <div class="field checkbox">
            <input
              type="checkbox"
              id="rebateDeclaration"
              v-model="rebateForm.rebateDeclaration"
              @change="v$.rebateForm.rebateDeclaration.$touch()"
              @keyup.enter.space="rebateForm.rebateDeclaration = !rebateForm.rebateDeclaration"
            />
            <label for="rebateDeclaration">
              Do you understand that giving false or misleading information is a serious offence?
              <span class="required-input">*</span>
            </label>
            <label for="rebateDeclaration" class="error" v-if="v$.rebateForm.rebateDeclaration.$error && v$.rebateForm.rebateDeclaration.sameAs.$invalid">
              Please include a response for Declaration.
            </label>
          </div>
        </FormField>
        <FormField labelText="Declaration Date" type="string" :required="false">
          <p>{{ getDeclarationDate }}</p>
        </FormField>
      </div>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, sameAs, requiredIf } from '@vuelidate/validators';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { CoverComposition } from '@general/js/enums';
import { RebateTierCode, RebateTierDisplayWithoutIncomeBrackets } from '@/enums';
import { isEmptyString } from '@/utils';
import { getCurrentDate, reverseFormatFormDate } from '@general/js/utils';
import SectionSeparatorWrapper from './SectionSeparatorWrapper.vue';
import FormField from './FormField.vue';
import MedicareCardForm from './MedicareCardForm.vue';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'RebateForm',

  components: {
    FormField,
    SectionSeparatorWrapper,
    MedicareCardForm,
    DropdownSelect,
  },

  props: {
    /** Include rebateTier options and myCirsumstance
     *  RebateTier options include tier name and description
     * */
    rebateTierInfo: {
      type: Array,
      required: true,
    },
    /** Applicants
     *  Includes: personCode, firstName, lastName, memberNumber, relationship
     * */
    rebateApplicants: {
      type: Array,
      required: true,
    },

    coverComposition: {
      type: String,
      required: true,
    },

    myCircumstances: {
      type: [String, Object], // can be 'single', 'other' or null
    },

    // Prefilled in change rebate applicant form
    rebateTierLevel: {
      type: [String, Object], // can be 'single', 'other' or null
    },

    rebateFormData: {
      type: Object,
    },

    helperText: {
      type: String,
      default: '',
    },

    // Helper text for Rebate Applicant field
    rebateApplicantFieldHelperText: {
      type: String,
      default: '',
    },

    // Helper text for `My household income for the purposes of determining rebate is` field
    householdIncomeFieldHelperText: {
      type: String,
      default: '',
    },

    medicareCardSectionSubText: {
      type: String,
      default: 'Please enter Medicare Card information to claim rebate.',
    },

    selectedApplicant: {
      type: [String, Object], // can be 'policyHolder', 'partner' or null
    },

    showRebateSectionHeader: {
      type: Boolean,
      default: true,
    },

    showRequiredFieldsText: {
      type: Boolean,
      default: true,
    },

    disableRebateApplicantDropdown: {
      type: Boolean,
      default: false,
    },

    isChangeCover: {
      type: Boolean,
      default: false,
    },
  },

  beforeMount() {
    if (this.rebateFormData !== null) {
      this.rebateForm = {
        ...this.rebateFormData,
        currentDate: reverseFormatFormDate(getCurrentDate()),
        medicareCard: {
          ...this.rebateFormData.medicareCard,
        },
      };
    }
    if (isEmptyString(this.rebateForm.myCircumstances)) {
      this.rebateForm.myCircumstances = this.myCircumstances;
    }

    if (!this.rebateForm.rebateTier || this.rebateTierLevel.toUpperCase() === RebateTierCode.NONE) {
      this.rebateForm.rebateTier = RebateTierCode.NONE;
    } else {
      this.rebateForm.rebateTier = this.rebateTierLevel;
    }
  },

  data() {
    return {
      rebateForm: {
        myCircumstances: null,
        isRebateApplied: null,
        isPolicyCovered: null,
        isMedicareEligible: null,
        rebateApplicant: null, // PersonCode
        currentDate: reverseFormatFormDate(getCurrentDate()),
        medicareCard: {
          cardType: null,
          cardNumber: null,
          firstName: '',
          lastName: '',
          expiryDate: null,
        },
        // Rebate information
        rebateTier: null, // "none|BASE_TIER|TIER_1|...",
        rebateAcceptance: false,
        rebateDeclaration: false,
      },
      isMedicareValid: false,
      applyRebateOptions: [
        { id: 'rebateApplied', value: true, displayValue: 'Yes' },
        { id: 'rebateNotApplied', value: false, displayValue: 'No' },
      ],
      policyCoveredOptions: [
        { id: 'policyCovered', value: true, displayValue: 'Yes' },
        { id: 'policyNotCovered', value: false, displayValue: 'No' },
      ],
      medicareEligibilityOptions: [
        { id: 'medicareEligibled', value: true, displayValue: 'Yes' },
        { id: 'medicareNotEligibled', value: false, displayValue: 'No' },
      ],
      myCircumstancesOptions: [
        { value: 'single', displayValue: "It's just me" },
        { value: 'other', displayValue: 'I have kids and/or a partner' },
      ],

      // Information Text
      policyCoveredInformationText: {
        isKidsOnly:
          '<p>A kids only policy cannot cover a responsible person.</p><p>If the responsible person requires cover a revised needs analysis is required.</p><p>To proceed confirm the cover is for kids only, and select No.</p>',
        other:
          'Applicants not covered by the policy cannot claim the Australian Government Rebate on Private Health Insurance (excluding child only policies) and employers and trustees of organisations cannot claim the Australian Government Rebate on Private Health Insurance on policies paid on behalf of employees. To apply for a rebate please select a covered adult to be the rebate applicant.',
      },
      policyEligibilityInformationText:
        '<p>All the people listed on the policy must be eligible to claim Medicare for you to receive the rebate as a reduced premium. Complete a new needs analysis, to proceed.</p>',
    };
  },

  computed: {
    isRebateAppliedAndEligible() {
      return this.rebateForm.isRebateApplied && this.rebateForm.isMedicareEligible;
    },

    isKidsOnly() {
      return this.coverComposition === CoverComposition.CHILDREN_ONLY;
    },

    // Get rebate tiers based on myCircumstances
    getRebateTierList() {
      let currentMyCircumstance;
      if (isEmptyString(this.rebateForm.myCircumstances)) {
        currentMyCircumstance = 'other';
      } else {
        currentMyCircumstance = this.rebateForm.myCircumstances;
      }

      const rebateTierOptions = this.rebateTierInfo.find((el) => {
        return el.myCircumstance === currentMyCircumstance;
      }).rebateTierOptions;
      const list = [];
      rebateTierOptions.forEach((op) => {
        if (op.dhl_code.toUpperCase() !== RebateTierCode.NONE) {
          list.push({
            value: op.dhl_code,
            name: `${RebateTierDisplayWithoutIncomeBrackets[op.dhl_code.toUpperCase()]} - ${op.dhl_name}`,
          });
        }
      });
      return list;
    },

    getDeclarationDate() {
      if (this.rebateForm.rebateAcceptance === true && this.rebateForm.rebateDeclaration === true) {
        return this.rebateForm.currentDate;
      }
      return null;
    },

    getApplicantsList() {
      const list = [];
      if (this.rebateApplicants.length > 0) {
        this.rebateApplicants.forEach((el) => {
          return list.push({ name: `${el.firstName} ${el.lastName}`, value: `${el.personCode}` });
        });
      }
      return list;
    },

    getInitialSelectedApplicant() {
      if (this.selectedApplicant === 'partner') {
        return this.getApplicantsList[1].name;
      }
      // Policy holder will be default one
      return this.getApplicantsList[0].name;
    },
  },

  validations() {
    return {
      rebateForm: {
        rebateApplicant: {
          required,
        },
        myCircumstances: {
          required,
        },
        rebateTier: {
          required: requiredIf(() => this.rebateForm.isRebateApplied && this.rebateForm.isMedicareEligible),
        },
        rebateAcceptance: {
          required,
          sameAs: sameAs(computed(() => this.requiredIfMedicareEligible())),
        },
        rebateDeclaration: {
          required,
          sameAs: sameAs(computed(() => this.requiredIfMedicareEligible())),
        },
        isPolicyCovered: {
          required: requiredIf(() => this.rebateForm.isRebateApplied),
        },
        isMedicareEligible: {
          required: requiredIf(() => this.rebateForm.isRebateApplied),
        },
      },
    };
  },

  watch: {
    // Whenever the fromResponse prop changes, save the changes into session storage
    rebateForm: {
      deep: true,
      handler() {
        this.$emit('rebateFormUpdated', {
          isFormValid: (!this.v$.rebateForm.$invalid) && this.isMedicareValid && this.rebateForm.rebateTier.toUpperCase() !== RebateTierCode.NONE,
          data: this.rebateForm,
        });
      },
    },

    'rebateForm.isRebateApplied': function (newValue, oldValue) {
      // If the value is false, reset all the data to null
      if (newValue !== oldValue && newValue === false) {
        this.clearMedicareCardData();
        this.clearRebateInfoData();
        this.rebateForm.isPolicyCovered = null;
        this.rebateForm.isMedicareEligible = null;
      }
    },
    'rebateForm.isPolicyCovered': function (val) {
      // If the value is false, reset all the data to null
      if ((val === false && !this.isKidsOnly) || (val === true && this.isKidsOnly)) {
        this.clearMedicareCardData();
        this.clearRebateInfoData();
        this.rebateForm.isMedicareEligible = null;
      }
    },
    'rebateForm.isMedicareEligible': function (newValue, oldValue) {
      // If the value is false, reset all the data to null
      if (newValue !== oldValue && newValue === false) {
        this.clearMedicareCardData();
        this.clearRebateInfoData();
      } else if (newValue === true) {
        this.getDefaultApplicantCode();
      }
    },

    'rebateFormData.myCircumstances': function (newValue, oldValue) {
      // If the value is false, reset all the data to null
      if (newValue !== oldValue && newValue !== null) {
        this.rebateForm.rebateTier = RebateTierCode.NONE;
      }
    },
  },

  methods: {
    // Reset Medicare Card data
    clearMedicareCardData() {
      this.rebateForm.medicareCard = {
        cardType: null,
        cardNumber: null,
        firstName: '',
        lastName: '',
        expiryDate: null,
      };
    },
    // Reset Rebate information data
    clearRebateInfoData() {
      this.clearDeclarationData();
    },
    // Reset declaration section
    clearDeclarationData() {
      this.rebateForm.rebateAcceptance = false;
      this.rebateForm.rebateDeclaration = false;
    },

    requiredIfMedicareEligible() {
      return this.rebateForm.isMedicareEligible === true;
    },

    getMedicareCardData(data) {
      if (data.formData !== undefined) {
        this.rebateForm.medicareCard = { ...data.formData };
      }
      if (data.isFormValid !== undefined) {
        this.isMedicareValid = data.isFormValid;
      }
    },

    getDefaultApplicantCode() {
      if (this.rebateApplicants.length > 0) {
        if (this.selectedApplicant === 'partner') {
          this.rebateForm.rebateApplicant = this.rebateApplicants[1].personCode;
        } else {
          // Policy holder will be default one
          this.rebateForm.rebateApplicant = this.rebateApplicants[0].personCode;
        }
      } else {
        this.rebateForm.rebateApplicant = null;
      }
    },

    getRebateTierInitialSelect(rebateTierVal) {
      if (rebateTierVal === RebateTierCode.NONE) {
        return undefined;
      }

      const rebateTierInfo = this.getRebateTierList.filter((tierDetails) => {
        return tierDetails.value === rebateTierVal;
      });

      return rebateTierInfo[0]?.name;
    },
  },
};
</script>

<style lang="scss" scoped>
.sub-heading-underlined {
  margin-bottom: 1rem;
}

.header {
  margin-bottom: 2rem;
}
</style>
